
import { defineComponent, PropType, ref } from 'vue';
import { VDataTable } from 'vuetify/lib';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

// Define the structure for individual history items based on API response
export interface HistoryItem {
  executionId: number;
  configId: number;
  configName: string;
  runDate: string; // ISO string
  startTime: string;
  status: 'completed' | 'failed' | string;
  totalItems?: number;
  totalValue?: number;
  totalPurchaseOrders?: number;
  errorMessage?: string;
}

// Define the structure for the data prop, including pagination
export interface GenerationHistoryData {
  rows: HistoryItem[];
  count: number; // Total number of items for pagination
}

// Vuetify DataTable options structure
interface DataTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
  groupBy: string[];
  groupDesc: boolean[];
  multiSort: boolean;
  mustSort: boolean;
}

// Define the slot props type
interface DataTableSlotProps {
  item: HistoryItem;
  index: number;
  headers: Array<{
    text: string;
    value: string;
    sortable?: boolean;
    align?: string;
  }>;
}

export default defineComponent({
  name: 'GenerationHistoryTable',
  props: {
    data: {
      type: Object as PropType<GenerationHistoryData>,
      required: true,
      default: () => ({ rows: [], count: 0 }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object as PropType<DataTableOptions>,
      default: () => ({ page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] }),
    }
  },
  emits: ['row-click', 'update:options', 'view-details', 'view-error', 'download-report', 'config-click'],
  setup(props, { emit }) {
    const headers = ref([
      { text: 'Config Name', value: 'configName', sortable: true },
      { text: 'Start Time', value: 'startTime', sortable: true },
      { text: 'Status', value: 'status', sortable: true },
      { text: 'Total Items', value: 'totalItems', sortable: true, align: 'end' },
      { text: 'Total Value', value: 'totalValue', sortable: true, align: 'end' },
      { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
    ]);

    const formatDate = (dateString: string): string => {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch (e) {
        return dateString; // Fallback
      }
    };

    const formatDuration = (seconds?: number): string => {
      if (seconds === undefined || seconds === null) return 'N/A';
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    };

    const formatCurrency = (value?: number): string => {
      if (value === undefined || value === null) return 'N/A';
      return CurrencyHelper.formatCurrency(value);
    };

    const getStatusColor = (status: string): string => {
      status = status.toLowerCase();
      if (status === 'completed') return 'green';
      if (status === 'failed') return 'red';
      if (status === 'running') return 'blue';
      return 'grey'; // Default color for other statuses
    };

    const handleRowClick = (item: HistoryItem) => {
      emit('row-click', item);
    };

    const handleOptionsUpdate = (newOptions: DataTableOptions) => {
      emit('update:options', newOptions);
    };

    const handleViewDetails = (item: HistoryItem) => {
      emit('view-details', item);
    };

    const handleViewError = (item: HistoryItem) => {
      emit('view-error', item);
    };

    const handleDownloadReport = (item: HistoryItem) => {
      emit('download-report', item);
    };

    const handleConfigClick = (item: HistoryItem) => {
      emit('config-click', item);
    };

    return {
      headers,
      formatDate,
      formatDuration,
      formatCurrency,
      getStatusColor,
      handleRowClick,
      handleOptionsUpdate,
      handleViewDetails,
      handleViewError,
      handleDownloadReport,
      handleConfigClick,
    };
  },
});
